import { useState } from "react"
import { format, differenceInDays, isValid } from "date-fns"
import {
  Users,
  Stethoscope,
  MessageSquare,
  BarChart3,
  Gauge, // Add this import for token count icon
} from "lucide-react"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { CartesianGrid, Line, LineChart, XAxis } from "recharts"
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import { useFetchDashboardStats } from "@/services/query/dashboard.query"
import { useToast } from "@/components/ui/use-toast"
import StatsCard from "./components/StatsCard"
import DateRangePicker from "@/components/shared/date-range-picker/date-range-picker"

const appointmentChartConfig = {
  total: {
    label: "Total",
    color: "#6366f1", // indigo-500
  },
  pending: {
    label: "Pending",
    color: "#f59e0b", // amber-500
  },
  approved: {
    label: "Approved",
    color: "#10b981", // emerald-500
  },
  rejected: {
    label: "Rejected",
    color: "#e3342f", // red-600 for rejected
  },
}

const orderChartConfig = {
  total: {
    label: "Total",
    color: "#6366f1", // indigo-500
  },
  completed: {
    label: "Completed",
    color: "#10b981", // emerald-500
  },
  pending: {
    label: "Pending",
    color: "#f59e0b", // amber-500
  },
}

const formatNumberWithSuffix = (num) => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}k`
  }
  return num.toString()
}

export default function DashboardPage() {
  const { toast } = useToast()
  const [dateError, setDateError] = useState("")
  const [date, setDate] = useState({
    from: new Date(new Date().setHours(0, 0, 0, 0) - 7 * 24 * 60 * 60 * 1000),
    to: new Date(new Date().setHours(23, 59, 59, 999)),
  })

  const handleDateChange = (newDate) => {
    setDateError("")

    if (!newDate?.from || !newDate?.to) {
      return
    }

    const from = new Date(newDate.from.setHours(0, 0, 0, 0))
    const to = new Date(newDate.to.setHours(23, 59, 59, 999))

    const daysDiff = differenceInDays(to, from)
    if (daysDiff > 30) {
      setDateError("Date range cannot exceed 30 days")
      toast({
        title: "Invalid date range",
        description: "Date range cannot exceed 30 days",
        variant: "destructive",
      })
      return
    }

    setDate({ from, to })
  }

  const formatDateSafely = (date) => {
    try {
      return date && isValid(date) ? format(date, "MMM d, yyyy") : ""
    } catch (error) {
      console.error("Date formatting error:", error)
      return ""
    }
  }

  const {
    data: dashboardData,
    isLoading,
    isError,
  } = useFetchDashboardStats(date?.from, date?.to)

  if (isLoading) {
    return <div className="p-6">Loading dashboard data...</div>
  }

  if (isError) {
    return <div className="p-6">Error loading dashboard data</div>
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-4 lg:py-6 min-h-screen">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 lg:mb-8 gap-4">
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
            Dashboard Overview
            <span className="block mt-1 text-base font-normal text-gray-500 dark:text-gray-400">
              {date?.from && date?.to
                ? `${formatDateSafely(date.from)} - ${formatDateSafely(date.to)}`
                : "Select date range to view statistics"}
            </span>
          </h1>
        </div>
        <div className="w-full sm:w-auto">
          <DateRangePicker
            date={date}
            onDateChange={handleDateChange}
            error={dateError}
            align="end"
            className="w-full sm:w-[320px]"
          />
          {dateError && (
            <p className="mt-1 text-sm text-red-500">{dateError}</p>
          )}
        </div>
      </div>

      <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 mb-6 lg:mb-8">
        <StatsCard
          title="Total Patients"
          value={dashboardData?.chat_stats?.[0]?.total_thread || 0}
          icon={MessageSquare}
          className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 hover:shadow-md transition-shadow"
        />
        <StatsCard
          title="Total Doctors"
          value={dashboardData?.total_doctors || 0}
          icon={Stethoscope}
          className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 hover:shadow-md transition-shadow"
        />

        <StatsCard
          title="Total Services"
          value={dashboardData?.total_services || 0}
          icon={BarChart3}
          className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 hover:shadow-md transition-shadow"
        />
      </div>

      <div className="grid gap-4 sm:gap-6 lg:gap-8 grid-cols-1 lg:grid-cols-3 xl:grid-cols-4">
        <Card className="lg:col-span-2 xl:col-span-3">
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <div>
                <CardTitle className="text-lg sm:text-xl">
                  Appointment Statistics
                </CardTitle>
                <CardDescription className="text-sm sm:text-base">
                  Overview of appointments status
                </CardDescription>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-indigo-500" />
                  <span>Total</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-amber-500" />
                  <span>Pending</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  <span>Approved</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-red-500" />
                  <span>Rejected</span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0 sm:p-2">
            <ChartContainer config={appointmentChartConfig}>
              <LineChart
                accessibilityLayer
                data={dashboardData?.appointment_stats || []}
                margin={{
                  top: 20,
                  right: 20,
                  bottom: 20,
                  left: 20,
                }}
                height={280}
                className="w-full [&_.recharts-cartesian-grid-horizontal_line]:stroke-gray-200 [&_.recharts-cartesian-grid-vertical_line]:stroke-gray-200"
              >
                <CartesianGrid
                  vertical={false}
                  className="stroke-gray-200 dark:stroke-gray-700"
                />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  className="text-xs sm:text-sm text-gray-600 dark:text-gray-400"
                />
                <ChartTooltip
                  cursor={false}
                  content={<ChartTooltipContent />}
                  wrapperClassName="!bg-white dark:!bg-gray-800 !shadow-lg !rounded-lg !border-none"
                />
                <Line
                  dataKey="total"
                  type="monotone"
                  stroke="#6366f1" // indigo-500
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  dataKey="pending"
                  type="monotone"
                  stroke="#f59e0b" // amber-500
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  dataKey="approved"
                  type="monotone"
                  stroke="#10b981" // emerald-500
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  dataKey="rejected"
                  type="monotone"
                  stroke="#e3342f" // emerald-500
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">AI Messages</CardTitle>
            <CardDescription>Recent AI interactions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <StatsCard
                title="AI Messages"
                value={dashboardData?.chat_stats?.[0]?.ai_message || 0}
                icon={Users}
                className="bg-gradient-to-br from-rose-50 to-red-50 dark:from-rose-900/20 dark:to-red-900/20 hover:shadow-md transition-shadow"
              />
              <StatsCard
                title="Token Count"
                value={formatNumberWithSuffix(
                  dashboardData?.chat_stats?.[0]?.token_count || 0
                )}
                icon={Gauge}
                className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 hover:shadow-md transition-shadow"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
